import React, { Context, ReactElement, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { useThrowError } from "@me8eon/throw_error";
import { capitalizeFirstLetter } from "@me8eon/string_utils";
import { ContextForStateConfig, GetterSetter, getterSetterWithDebug, makeUseStateChild, Setter } from "@me8eon/context";
import { AsyncState, exceptionToAsyncState, isDataAs } from "@me8eon/async";
import { ParentAndChildrenWithIds, ParentAndOptionalChild, ParentAsyncStorage } from "@me8eon/async_storage";
import { ErrorsOr, flatMapErrorsOrK, isErrors, isValue, mapErrorsOr } from "@me8eon/errors";
import { DebugLog, useDebug } from "@me8eon/debug";
import { sessionsAndMessagesDebugName } from "@me8eon/sessions_messages";

// A parent child async storage context for React.

//This is for things like 'sessions and messages' where we have a parent (session) and children (messages).
//It can be used anywhere we have a parent and children relationship, where the parent can have multiple children and we want to manage their state asynchronously.
//There is the concept of a current child, which is the one that is currently being viewed or edited as well as the current parent.


export type ParentChildAsyncContextData<ParentId, Parent, ChildId, Child> = {
    parentChildOps: GetterSetter<AsyncState<ParentAndChildrenWithIds<ParentId, Parent, ChildId, Child>>>;
    store: ParentAsyncStorage<ParentId, Parent, ChildId, Child>
    ops: ParentChildAsyncOps<ParentId, Parent, ChildId, Child>;
}
export type ParentChildAsyncOps<ParentId, Parent, ChildId, Child> = {
    deleteChild: (...ids: ChildId[]) => Promise<ErrorsOr<ParentAndOptionalChild<Parent, ChildId, Child>>>
}

export type ParentChildContextResults<FIELD extends string, ParentId, Parent, ChildId, Child> =
    {
        use: () => GetterSetter<ParentAndChildrenWithIds<ParentId, Parent, ChildId, Child>>;
        useAsync: () => GetterSetter<AsyncState<ParentAndChildrenWithIds<ParentId, Parent, ChildId, Child>>>;
        ops: () => ParentChildAsyncOps<ParentId, Parent, ChildId, Child>;
        Provider: (props: ParentAndChildProviderProps<FIELD, ParentId, Parent, ChildId, Child>) => ReactElement;
        context: Context<ParentChildAsyncContextData<ParentId, Parent, ChildId, Child> | undefined>;
    }


/** Note the useId is probably a custom hook. Like 'useEmail'. AS this changes, the context will update automatically. */
type ParentAndChildProviderProps<FIELD extends string, ParentId, Parent, ChildId, Child> = {
    children: ReactNode,
    useId: () => ParentId
} & Record<FIELD, ParentAsyncStorage<ParentId, Parent, ChildId, Child>>;

export function makeContextForParentChildAsyncState<FIELD extends string, ParentId, Parent, ChildId, Child, >(field: FIELD, { allowedUndefined = false, debug = false, showStackTrace = false }: ContextForStateConfig = {}): ParentChildContextResults<FIELD, ParentId, Parent, ChildId, Child> {

    const Context = React.createContext<ParentChildAsyncContextData<ParentId, Parent, ChildId, Child> | undefined>(undefined);

    function getContextValueReportingAnyErrors() {
        const contextValue = useContext(Context);
        const reportError = useThrowError();
        if (contextValue === undefined && !allowedUndefined) {
            const fieldWithCap = capitalizeFirstLetter(field);
            reportError("s/w", `use${fieldWithCap} must be used within a ${fieldWithCap}Provider`);
        }
        return contextValue;
    }

    const useAsync = (): GetterSetter<AsyncState<ParentAndChildrenWithIds<ParentId, Parent, ChildId, Child>>> =>
        getContextValueReportingAnyErrors()!.parentChildOps;
    const ops = (): ParentChildAsyncOps<ParentId, Parent, ChildId, Child> => getContextValueReportingAnyErrors()!.ops;
    const useField = makeUseStateChild(useAsync, lb => lb.focusOnSingleKeyVariant("data"));


    type AsyncData = AsyncState<ParentAndChildrenWithIds<ParentId, Parent, ChildId, Child>>

    function addWriteThroughToGetterSetter<T>([oldValue, rawSetValue]: GetterSetter<AsyncData>,
                                              store: ParentAsyncStorage<ParentId, Parent, ChildId, Child>, debug: DebugLog): GetterSetter<AsyncData> {
        const setValue: Setter<AsyncData> =
            async (newValue) => {
                const value: AsyncState<ParentAndChildrenWithIds<ParentId, Parent, ChildId, Child>> =
                    typeof newValue === "function" ? (newValue as any)(oldValue) : newValue;
                debug(`addWriteThroughToGetterSetter.setValue:`, field, value, `oldValue:`, oldValue);
                rawSetValue(value);
                if (isDataAs(value)) {
                    debug(`addWriteThroughToGetterSetter: storeParent ${field} with parentId:`, value.data.parentId);
                    const res = await store.storeParentWithChild(value.data);
                    if (!isValue(res)) { // If storing the parent fails, we log the error and set the state to reflect the failure. This may surface to the UI as an error state.
                        console.error(`addWriteThroughToGetterSetter: storeParentWithChild ${field} result:`, res);
                        rawSetValue({ errors: res.errors });
                        return res;
                    }
                }
                return value;
            };
        return [oldValue, setValue];
    }

    function FieldProvider(props: ParentAndChildProviderProps<FIELD, ParentId, Parent, ChildId, Child>) {
        const store: ParentAsyncStorage<ParentId, Parent, ChildId, Child> = props[field];
        const debugLog = useDebug(sessionsAndMessagesDebugName);
        const rawGetterSetter = useState<AsyncState<ParentAndChildrenWithIds<ParentId, Parent, ChildId, Child>>>({ loading: true });
        const parentChildOps = useMemo(() =>
                addWriteThroughToGetterSetter(debug ? getterSetterWithDebug(rawGetterSetter, field, showStackTrace) : rawGetterSetter, store, debugLog),
            [store, rawGetterSetter, field, debug, showStackTrace]);
        const [state, setState] = parentChildOps;
        const parentId = props.useId();
        if (debug) debugLog(`${capitalizeFirstLetter(field)}Provider:`, store);
        useEffect(() => {
            debugLog(`${capitalizeFirstLetter(field)}Provider: useEffect called with parentId:`, parentId);
            let mounted = true;
            if (!parentId) {
                debugLog(`${capitalizeFirstLetter(field)}Provider: No parentId provided. Not fetching data.`);
                setState({ errors: [`No parentId provided to ${field}Provider`] });
                return;
            }
            if (debug) debugLog(`making loading true for ${field} with parentId ${parentId}`);
            setState({ loading: true });
            store.getParentAndChild(parentId, false)//note: throws errors if not found.
                .then(result => {
                    const newValue = isErrors(result) ? { errors: result.errors } : { data: result.value };
                    if (debug) debugLog(`fetched new value. Mounted is ${mounted}`, field, newValue);
                    mounted && setState({ ...newValue });
                })
                .catch(e => {
                    console.error(`Error fetching data for ${field} with parentId ${parentId}:`, e);
                    return mounted && setState(exceptionToAsyncState(e));
                });
            return () => {mounted = false;};
        }, [parentId, store]);

        const value: ParentChildAsyncContextData<ParentId, Parent, ChildId, Child> = useMemo(() => {
            const ops: ParentChildAsyncOps<ParentId, Parent, ChildId, Child> = {
                deleteChild: async (...ids: ChildId[]) => {
                    const [currentState] = rawGetterSetter; // get fresh state at call time
                    if (!isDataAs(currentState)) return { errors: [`Cannot delete child: state is not data. State is ${JSON.stringify(currentState)}`] };

                    const { parent, parentId } = currentState.data;
                    // Delete the child(ren) and update the parent in storage
                    const result = await store.deleteChildAndUpdateParentAndMaybeNewChild(parent, parentId, ids);
                    if (isErrors(result)) return result;

                    const { parent: newParent, childAndId } = result.value;

                    // Update the context state if everything succeeded
                    setState(old => {
                        if (!isDataAs(old)) return old;
                        const result = {
                            data: childAndId
                                ? { ...old.data, parent: newParent, childId: childAndId.childId, child: childAndId.child }
                                : { ...old.data, parent: newParent },
                        };
                        return result;
                    });
                    return result;
                },

            };
            return { store, parentChildOps, ops };
        }, [store, rawGetterSetter]);

        return <Context.Provider value={value}>{props.children}</Context.Provider>;
    }

    return { use: useField, useAsync, ops, Provider: FieldProvider, context: Context };
}